import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useSite } from "@/lib/site";
import Seo from "@/components/Seo";
import Breadcrumb from "@/components/Breadcrumb";
import { Reveal } from "@/components/Reveal";
import { IdentityLabel, MetaTag, SectionLabel } from "@/components/ui-bits";

export default function Portfolio() {
  const { data: items = [] } = useQuery({ queryKey: ["portfolio"], queryFn: async () => (await api.get("/portfolio")).data });
  const { data: clients = [] } = useQuery({ queryKey: ["clients"], queryFn: async () => (await api.get("/clients")).data });
  const { pageContent } = useSite();
  const content = pageContent.portfolio || {};
  const selectedGallery = (content.gallery_images || []).filter(Boolean);
  const fallbackGallery = items.flatMap((item) => [...(item.gallery || []), item.cover_image]).filter(Boolean);
  const gallery = [...new Set(selectedGallery.length > 0 ? selectedGallery : fallbackGallery)].slice(0, 10);
  const clientTitle = (content.clients_title || "Dipercaya oleh {count} brand di Medan").replace("{count}", clients.length);
  return (
    <div data-testid="portfolio-page">
      <Seo title="Portfolio & Case Studies | Digitalis Medan" description="Case studies dan karya digital marketing Digitalis untuk brand di Medan: social media content, campaign, dan lainnya." canonicalPath="/portfolio" />
      <section className="mx-auto max-w-[1400px] px-5 md:px-10 pt-10 md:pt-14 pb-10 md:pb-14">
        <Breadcrumb items={[{ label: "Home", to: "/" }, { label: "Portfolio" }]} />
        <div className="mt-7"><IdentityLabel>{content.eyebrow || "Bukti Kerja"}</IdentityLabel><h1 className="font-display font-extrabold tracking-tightest leading-tight text-[2.35rem] sm:text-5xl md:text-6xl">{content.title || "Karya & Case Study"}</h1></div>
        <p className="text-muted-foreground text-base md:text-lg max-w-xl mt-4">{content.description || "Lihat pendekatan visual, strategi, dan hasil yang kami bangun bersama brand."}</p>
      </section>

      {content.show_gallery !== false && gallery.length > 0 && (
        <section className="border-t border-border py-12 md:py-18">
          <div className="mx-auto max-w-[1400px] px-5 md:px-10">
            <div className="mb-7 grid gap-3 md:grid-cols-[1fr_0.8fr] md:items-end">
              <div><SectionLabel>Galeri Portfolio</SectionLabel><h2 className="font-display text-3xl font-extrabold tracking-tightest md:text-5xl">{content.gallery_title || "Galeri Karya Digitalis"}</h2></div>
              <p className="max-w-xl text-sm leading-relaxed text-muted-foreground md:text-base">{content.gallery_description || "Kumpulan visual pilihan dari project yang telah dikerjakan."}</p>
            </div>
            <div className="columns-2 gap-3 md:columns-3 md:gap-5 lg:columns-4" data-testid="portfolio-gallery">
              {gallery.map((image, index) => (
                <Reveal key={`${image}-${index}`} className="mb-3 break-inside-avoid overflow-hidden rounded-2xl border border-border bg-secondary md:mb-5 sm:rounded-3xl">
                  <img src={image} alt={`Galeri karya Digitalis ${index + 1}`} className="block h-auto w-full transition-transform duration-700 hover:scale-[1.02]" loading={index < 2 ? "eager" : "lazy"} />
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {content.show_clients !== false && clients.length > 0 && (
        <section className="border-t border-border bg-surface py-12 md:py-16" data-testid="portfolio-clients">
          <div className="mx-auto max-w-[1400px] px-5 md:px-10">
            <div className="grid gap-7 lg:grid-cols-[0.7fr_1.3fr] lg:items-center">
              <div><SectionLabel>Kolaborasi Brand</SectionLabel><p className="font-display text-6xl font-extrabold tracking-tightest text-primary md:text-7xl">{clients.length}</p><h2 className="mt-2 max-w-md font-display text-2xl font-extrabold tracking-tightest md:text-4xl">{clientTitle}</h2><p className="mt-3 max-w-md text-sm text-muted-foreground">{content.clients_description}</p></div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4">
                {clients.map((client) => (
                  <a key={client.id} href={client.website || undefined} target={client.website ? "_blank" : undefined} rel="noopener noreferrer" className="flex min-h-24 items-center justify-center rounded-2xl border border-border bg-background p-4 sm:min-h-28 sm:rounded-3xl" aria-label={client.name}>
                    {client.logo ? <img src={client.logo} alt={`Logo ${client.name}`} className="max-h-12 max-w-[82%] object-contain sm:max-h-14" loading="lazy" /> : <span className="text-center font-display text-sm font-bold leading-tight sm:text-base">{client.name}</span>}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {content.show_projects !== false && <section className="border-t border-border">
        <div className="mx-auto grid max-w-[1400px] grid-cols-2 gap-x-3 gap-y-8 px-5 py-10 sm:gap-6 md:px-10 md:py-14 md:gap-8 lg:grid-cols-3">
          {items.map((p, i) => (
            <Reveal key={p.id} delay={(i % 3) * 0.06} className="min-w-0">
              <Link to={`/portfolio/${p.slug}`} className="group block min-w-0" data-testid={`portfolio-item-${p.slug}`}>
                <div className="relative mb-3 aspect-[4/3] overflow-hidden rounded-2xl border border-border soft-shadow sm:mb-4 sm:rounded-3xl">
                  <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700" loading="lazy" />
                </div>
                <MetaTag className="mb-2">{p.industry} · {p.year}</MetaTag>
                <h2 className="break-words font-display text-sm font-bold leading-tight transition-colors group-hover:text-primary sm:text-lg md:text-xl">{p.title}</h2>
                <p className="mt-1.5 hidden text-sm text-muted-foreground line-clamp-2 sm:block">{p.summary}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>}
    </div>
  );
}
